export type BrandTabId =
  | "details"
  | "plans"
  | "services"
  | "form"
  | "configuration"
  | "guidelines";

/**
 * The two pieces of writing a plan carries with it, and the Guidelines &
 * policy tab switches between. Guidelines are what a client is told about
 * eating on a plan; the policy is the terms every plan is sold under.
 */
export type PolicyDocumentId = "guidelines" | "policy";

/**
 * Both, as the rich text editor writes them - HTML, and `""` where nothing
 * has been written. The drafts: one record rather than two because they are
 * saved together - the tab has one Save row, and it saves whichever of the
 * two is off its stored copy.
 */
export type PolicyDocuments = Record<PolicyDocumentId, string>;

/**
 * A clause on the policy: a duration-conditioned sentence - "Weight
 * Management guidance *after completion of* 3 Months" - the body embeds by
 * writing `{{clause:N}}` where `N` is the clause's `position`. The sentence
 * itself is put together by the server, in `resolvedBody`; a clause here is
 * only the two things it is made of. Guidelines never carry any.
 */
export interface PolicyClause {
  /** The practitioner's own words. Never blank. */
  label: string;
  /** From the same vocabulary a plan's duration comes from. */
  duration: PlanDuration;
  /**
   * The integer the body's `{{clause:N}}` names. Chosen when the body is
   * written - not a database id - and unique within one publish.
   */
  position: number;
}

/**
 * One of the two documents as the server holds it - the Guidelines & policy
 * tab's stored copy, which its drafts open on and Cancel goes back to.
 */
export interface PolicyDocument {
  id: PolicyDocumentId;
  /** What the editor loads. Any `{{clause:N}}` in it is left as written. */
  body: string;
  /**
   * The same, with every `{{clause:N}}` replaced by its sentence - what a
   * client reads. Drawn as given; the replacing is the server's alone.
   */
  resolvedBody: string;
  /** Always a list; `[]` for the guidelines and for a policy with none. */
  clauses: PolicyClause[];
  /**
   * Counts up by one on every publish and never comes back down - not even
   * after a reset. `0` only alongside `isCustom: false`.
   */
  version: number;
  /** `false` while the workspace is on the system default, never having published. */
  isCustom: boolean;
}

/** Both documents as stored. */
export type StoredPolicyDocuments = Record<PolicyDocumentId, PolicyDocument>;

/**
 * The wire's name for each document - `/api/v1/guidelines/` answers
 * `GUIDELINES`, `/api/v1/policy/` answers `POLICY`.
 */
export type PracticeDocumentType = "GUIDELINES" | "POLICY";

/** A clause as the server answers it: the duration expanded, not an id. */
export interface PracticeDocumentClauseResponse {
  label: string;
  package_duration: PlanDuration;
  position: number;
}

/**
 * What every `GET` and `PUT` on `/api/v1/guidelines/` and `/api/v1/policy/`
 * answers with, and the two `reset/` `POST`s too. `clauses` is always
 * present, so nothing branches on the key.
 */
export interface PracticeDocumentResponse {
  document_type: PracticeDocumentType;
  body: string;
  resolved_body: string;
  clauses: PracticeDocumentClauseResponse[];
  version: number;
  is_custom: boolean;
}

/** A clause as it is sent: the duration by its id. */
export interface PracticeDocumentClauseRequest {
  label: string;
  package_duration_id: number;
  position: number;
}

/**
 * `PUT /api/v1/guidelines/` - the whole body, every time; a publish is a
 * full replace, not a patch. No `clauses` key at all: the server refuses the
 * key's mere presence, `[]` included, with `guidelines_cannot_have_clauses`.
 */
export interface PublishGuidelinesRequest {
  body: string;
}

/**
 * `PUT /api/v1/policy/` - the whole body and the whole clause list, every
 * time. Every `{{clause:N}}` in the body must have one clause at
 * `position: N` and every clause must be named by one token, or the whole
 * publish is refused with `clause_token_mismatch` and nothing is written.
 */
export interface PublishPolicyRequest {
  body: string;
  clauses?: PracticeDocumentClauseRequest[];
}

/** The two documents the right-hand panel can preview. */
export type BrandPreviewId = "meal-plan" | "invoice";

/**
 * The two client-facing forms the Form tab can preview.
 *
 * Both are reached through the one link the tab hands out, which is why they
 * are a preview switch and not two links: an enquiry form is open to anyone
 * who has the address, and a pre-consultation form opens for a client who has
 * already been sent a one-time link of their own from their profile.
 */
export type FormPreviewId = "enquiry" | "pre-consultation";

/**
 * The address a practitioner's shared forms answer at, as the backend holds
 * it - `/api/v1/enquiry-connect-link/`, which calls it a connect link.
 *
 * `shareUrl` is the whole address and is the backend's to build: it knows
 * where it is serving from and this app does not. Nothing should assemble the
 * address out of `slug` when there is one of these to hand.
 *
 * `isActive` is the backend's switch for whether the link answers at all.
 * Nothing on this screen sets it - there is no control for it in the design -
 * so it is carried rather than used.
 */
export interface ConnectLink {
  slug: string;
  isActive: boolean;
  shareUrl: string;
}

/**
 * What an upload is for. The backend files it under the workspace by this, so
 * it is not a label — sending the wrong one puts the QR where the logo goes.
 *
 * `PROFILE_PHOTO` belongs to the Settings screen rather than this one. It is
 * declared here because the purposes are one closed set the backend keeps, and
 * `uploadFile` — which every screen uploads through — takes the whole set.
 */
export type FilePurpose = "BRAND_LOGO" | "PAYMENT_QR" | "PROFILE_PHOTO";

/** `POST /api/v1/files/` as the app reads it, once the casing is mapped. */
export interface StoredFile {
  /** The id the backend knows the file by, and what a save refers to it by. */
  publicId: string;
  /** Where it is served from, relative to the API origin. */
  url: string;
  contentType: string;
  byteSize: number;
}

/**
 * A file the dietitian picked, uploaded, and now part of the form.
 *
 * `dataUrl` is the local read of the bytes, which is what the previews paint —
 * instant, and needing no token to fetch. `publicId` and `url` are the
 * backend's, and are what saving the brand details sends. `name` is kept for
 * the row that offers to replace or remove it.
 */
export interface UploadedFile {
  name: string;
  dataUrl: string;
  publicId: string;
  url: string;
}

/** Everything the Details tab collects — one flat shape, one setState. */
export interface BrandDetails {
  fullName: string;

  brandName: string;
  logo: UploadedFile | null;

  phone: string;
  whatsapp: string;
  /** While set, `whatsapp` is kept in step with `phone` and is not editable. */
  whatsappSameAsPhone: boolean;
  email: string;
  address: string;
  state: string;
  city: string;
  pincode: string;

  accountHolder: string;
  accountNumber: string;
  bankName: string;
  ifsc: string;
  upiNumber: string;
  upiId: string;
  paymentQr: UploadedFile | null;
}

export type BrandTextField = {
  [K in keyof BrandDetails]: BrandDetails[K] extends string ? K : never;
}[keyof BrandDetails];

/* ── Wire shapes ───────────────────────────────────────────────────────────
   snake_case exactly as the backend sends it. Only `brandService` should
   touch these; it maps them into `BrandDetails` so the rest of the app never
   sees the API's casing.

   A file is a `public_id` on the way up and the whole record on the way back,
   which is why each appears twice under a different type.               */

/** A stored file as any response embeds it. */
export interface StoredFileResponse {
  public_id: string;
  url: string;
  content_type: string;
  byte_size: number;
  /**
   * The name the file was uploaded under - `image (12).png`, not the id it is
   * stored as. What the row listing the file should say, and the only part of
   * the record a person would recognise.
   *
   * Optional because a record stored before the backend started keeping it has
   * none, which is what the fall back to the URL in `brandService` is for.
   */
  original_name?: string;
}

/** Body of `PATCH /api/v1/brand/`. */
export interface BrandProfileRequest {
  brand_name: string;
  contact_name: string;
  phone_number: string;
  whatsapp_number: string;
  email: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  /** The `public_id` of an upload, or `null` where the logo was removed. */
  logo: string | null;
}

/** Body of `PATCH /api/v1/payment-configuration/`. */
export interface PaymentConfigurationRequest {
  account_holder_name: string;
  account_number: string;
  bank_name: string;
  ifsc: string;
  upi_number: string;
  upi_id: string;
  /** The `public_id` of an upload, or `null` where the QR was removed. */
  payment_qr: string | null;
}

/**
 * What each `PATCH` answers with: the record as it now stands, wrapped in a
 * key naming it, and with the file expanded from the id that was sent.
 *
 * The values are the backend's own rather than what was posted - it dials the
 * phone numbers to `+91…`, upper-cases the IFSC, lower-cases the email and the
 * UPI address - so the response, not the request, is what the form takes back.
 */
export interface BrandProfileResponse {
  brand_profile: Omit<BrandProfileRequest, "logo"> & {
    logo: StoredFileResponse | null;
  };
}

export interface PaymentConfigurationResponse {
  payment_configuration: Omit<PaymentConfigurationRequest, "payment_qr"> & {
    payment_qr: StoredFileResponse | null;
  };
}

/** A plan is listed on the public page while it is `Active`. */
export type PlanStatus = "Active" | "In-active";

/**
 * A way the practice supports a client over the run of a plan, as the
 * backend spells them. The codes travel; the words below are only ever for the
 * screen, which is why the dialog picks by label and saves by value.
 */
export type SupportChannel = "EMAIL" | "WHATSAPP" | "CALL";

/** The three, in the order the dialog offers them. */
export const SUPPORT_CHANNELS: ReadonlyArray<{
  value: SupportChannel;
  label: string;
}> = [
  { value: "EMAIL", label: "Email" },
  { value: "WHATSAPP", label: "Whatsapp" },
  { value: "CALL", label: "Call" },
];

export interface BrandPlan {
  /** The `public_id` the backend knows it by, and what an edit addresses. */
  id: string;
  name: string;
  /** The duration's label — what the table shows, e.g. "3 Months". */
  duration: string;
  /**
   * The duration's id, which is what a save sends. Carried alongside the label
   * rather than looked up from it: the labels are the backend's to rename, and
   * matching on one would put a save at the mercy of its wording.
   */
  durationId: number;
  /** Rupees as a plain number; the table is what formats and groups it. */
  price: number;
  /**
   * What the plan includes, where a number has been put on it.
   *
   * `null` rather than 0 throughout: a plan that does not say how many meal
   * plans it comes with is not a plan that comes with none, and the two
   * have to stay tellable apart — the backend keeps the distinction, so this
   * side does too.
   */
  consultations: number | null;
  mealPlans: number | null;
  mealOptionsPerMeal: number | null;
  /** The channels support is offered over; empty where none were picked. */
  supportChannels: SupportChannel[];
  createdOn: string;
  status: PlanStatus;
}

/** What the create dialog hands back — the rest of the row is server-set. */
export type NewBrandPlan = Pick<
  BrandPlan,
  | "name"
  | "duration"
  | "durationId"
  | "price"
  | "consultations"
  | "mealPlans"
  | "mealOptionsPerMeal"
  | "supportChannels"
>;

/**
 * Body of `POST /api/v1/packages/`, and of a `PATCH` to one.
 *
 * Every field is optional because the same shape serves both: a create sends
 * all of them, an edit only what changed.
 */
export interface PlanRequest {
  name?: string;
  description?: string;
  /** The duration's id, not its label — see `BrandPlan.durationId`. */
  duration?: number;
  /** Rupees to two decimal places, as a string: `"2999.00"`. */
  price_amount?: string;
  /**
   * What the plan includes. `null` is a value here rather than an omission
   * — it is how a count already stored gets cleared, which leaving the key out
   * of a `PATCH` would not do.
   */
  consultations_count?: number | null;
  meal_plans_count?: number | null;
  meal_options_per_meal?: number | null;
  support_channels?: SupportChannel[];
}

/**
 * What both verbs answer with: the plan as the backend now holds it, with
 * the duration expanded from the id that was sent.
 */
export interface PlanResponse {
  public_id: string;
  name: string;
  duration: PlanDuration | null;
  price_amount: string;
  /** `"INR"`. Not shown anywhere yet — the table's ₹ is written into it. */
  currency: string;
  /** The Active / In-active pill, as the backend keeps it. */
  is_active: boolean;
  consultations_count: number | null;
  meal_plans_count: number | null;
  meal_options_per_meal: number | null;
  /**
   * Sent back as the codes that went up. A code this app has never heard of is
   * carried through rather than dropped — the dialog shows it as its own chip,
   * which is the only way an edit can save without silently deleting it.
   */
  support_channels: SupportChannel[] | null;
  created_at: string;
  updated_at: string;
}

/**
 * A plan name the backend offers, from `/api/v1/package-name-suggestions/`.
 *
 * A suggestion and not a rule: the dialog's "Add custom plan" row is there
 * precisely for the name this list does not hold, and what gets saved is the
 * text, not the id — so the id is kept only because the endpoint sends it.
 */
export interface PlanSuggestion {
  id: number;
  name: string;
}

/**
 * A length a plan can run for, from `/api/v1/package-durations/`.
 *
 * `label` rather than `name`, which is how the endpoint sends it.
 */
export interface PlanDuration {
  id: number;
  label: string;
}

/* ── Services ───────────────────────────────────────────────────────────
   What the practice *does*, as against what it sells. A plan is a length and
   a price somebody buys; a service is the named work behind it — "Weight
   loss", "Sleep improvement" — and it is what the public page lists so a
   visitor can see whether this practice handles what they came for.

   The two are deliberately separate records: a practice offers a handful of
   services and prices many plans against them, and folding one into the
   other is what made "service" mean two things here before.

   The backend words a plan differently again: a service lives at
   `/api/v1/practitioner-services/`, and `/api/v1/packages/` is where the plans
   are. The names on this side are the app's, and `API_ENDPOINTS` is where the
   two are reconciled — nothing below carries the URL's meaning. */

/** A service is listed on the public page while it is `Active`. */
export type ServiceStatus = "Active" | "In-active";

/**
 * The goal a curated service was made from, as `/api/v1/goals/` sends it —
 * the same `{ id, name }` rows the client form's Goal field picks from.
 *
 * A service is either one of these, chosen from the list, or a custom one
 * named in the practitioner's own words with no goal behind it. Which of the
 * two it is decides what a create posts: see `ServiceRequest`.
 */
export interface ServiceGoal {
  id: number;
  name: string;
}

export interface BrandService {
  /** The `public_id` the backend knows it by. */
  id: string;
  name: string;
  /**
   * The line under the name on the card — what this service is, in the
   * practitioner's own words. Optional: the dialog does not wait on it, and a
   * service with none draws a card with nothing where it would go.
   */
  description: string;
  /**
   * The goal behind it, or `null` on a custom one. Nothing draws it yet — the
   * card shows the name, which for a curated service is the goal's — but it is
   * what says which of the two kinds this record is, and an edit that ever
   * lands needs it to send the service back the way it arrived.
   */
  goal: ServiceGoal | null;
  /** Already formatted for the card — `1 Jul 2026`. See `formatCreatedOn`. */
  createdOn: string;
  status: ServiceStatus;
}

/**
 * What the create dialog hands back. The rest of the card — the id, the date,
 * the status — is the server's to set.
 *
 * `goal` is what tells a curated service from a custom one: the row that was
 * picked from the suggestions, or `null` where the name was typed. The whole
 * row rather than its id, so a create can tell a name left as the goal's from
 * one edited away from it without going back to the list to look it up.
 */
export interface NewBrandService {
  name: string;
  description: string;
  goal: ServiceGoal | null;
}

/**
 * What a create posts. Three shapes in one type, and which one it is depends
 * on `goal_id`:
 *
 *   { goal_id, description }          curated — the server names it after
 *                                     the goal, so `name` is left out
 *   { goal_id, name, description }    curated, renamed at creation
 *   { name, description }             custom — no goal behind it
 *
 * Sending a `name` that is already the goal's own is the difference between a
 * service the backend named and one the practitioner did, so `createService`
 * omits it unless the two actually differ.
 *
 * An edit sends the same type without the `goal_id`: the `PATCH` takes the
 * name and the description and nothing else, so the goal behind a curated
 * service is fixed at creation. That is why the details dialog offers no way
 * to change it — not an omission, but the endpoint.
 */
export interface ServiceRequest {
  goal_id?: number;
  name?: string;
  description?: string;
}

/**
 * The service as every one of the endpoints answers with it — the list, the
 * create, the read, the edit, and the two that list it or take it down.
 *
 * The timestamps are optional because the `PATCH` has been seen to answer
 * without them. That is not a field this app can do without: the card shows a
 * created date, and a `new Date(undefined)` renders as "Invalid Date" in it.
 * So a response is merged over the card already in hand rather than replacing
 * it, and `toBrandService` is where that happens.
 */
export interface ServiceResponse {
  public_id: string;
  name: string;
  /** Empty string where the practitioner wrote none, not `null`. */
  description: string;
  /** The Active / In-active pill, as the backend keeps it. */
  is_active: boolean;
  /** The goal it was made from, or `null` on a custom service. */
  goal: ServiceGoal | null;
  created_at?: string;
  updated_at?: string;
}
